import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import type { Tabs, Ticket, TicketStatus, TicketLeg } from '@core/Areas/AccountActivity/types'
import { isSameRaceMulti } from './legTypes'

export function getStatusText(status: TicketStatus) {
  switch (status) {
    case 'NoPayout':
      return 'No Payout'
    case 'Abandoned':
      return 'Abandoned - Refunded'
    case 'Scratched':
      return 'Scratched - Refunded'
    case 'Cancelled':
      return 'Cancelled - Refunded'
    default:
      return status
  }
}

export const getTicketStatus = (
  ticket: Ticket | BetLoopShareTicket,
  activeTab: Tabs | null
): TicketStatus => {
  const isCancelled = ticket.status === 'Cancelled'
  const isWin = ticket.status === 'Resolved' && ticket.payValue > 0
  const isBettingClosed = ticket.status === 'BettingClosed'
  const isPending = activeTab === 'pending' || activeTab === 'rebet'
  const noPayout = ticket.payValue === 0
  const isAbandoned = ticket.legs?.every((leg: TicketLeg) => leg.result === 'ABD')
  const isScratched = ticket.legs?.every((leg: TicketLeg) => leg.result === 'SCR')

  const isRefund =
    ticket.legs?.every(isScratchedOrAbandoned) ||
    (isSameRaceMulti(ticket) && ticket.legs?.some(isScratchedOrAbandoned))

  const isPartiallyRefunded =
    // partial refunds never apply to SRM
    !isSameRaceMulti(ticket) &&
    ticket.payValue > 0 &&
    // at least 1 leg is scratched OR abandoned
    ticket.legs?.some(isScratchedOrAbandoned) &&
    // at least 1 leg is NOT scratched AND NOT abandoned
    ticket.legs?.some(leg => !isScratchedOrAbandoned(leg))

  const isMultiWithAllWinsAndPartialRefund =
    isPartiallyRefunded &&
    ticket.legs?.length > 1 &&
    isWin &&
    ticket.legs.some(x => x.result === 'WIN') &&
    !ticket.legs.some(x => x.result === 'LOSE' || x.result === '-' || !x.result)

  return (
    (isMultiWithAllWinsAndPartialRefund && 'Won') ||
    (isBettingClosed && 'Closed') ||
    (isPartiallyRefunded && 'Partially Refunded') ||
    (isAbandoned && 'Abandoned') ||
    (isScratched && 'Scratched') ||
    (isRefund && 'Refunded') ||
    (isWin && 'Won') ||
    (isCancelled && 'Cancelled') ||
    (isPending && 'Pending') ||
    (noPayout && 'NoPayout') ||
    null
  )
}

function isScratchedOrAbandoned(leg: TicketLeg) {
  return leg.result === 'SCR' || leg.result === 'ABD'
}

// in addition to the standard 'Refunded' state, this method includes the 'psuedo refunded' states (abandoned, scratched, and cancelled) which are derived separately elsewhere, e.g. footer.tsx
const refundedTicketStatuses: TicketStatus[] = ['Refunded', 'Abandoned', 'Scratched', 'Cancelled']

export function isRefundedStatus(ticket: Ticket | BetLoopShareTicket): boolean {
  return refundedTicketStatuses.includes(getTicketStatus(ticket, null))
}
